/**
 * LexisNexisScreen — Risk Mgmt Panel.
 *
 * Risk Management owns the FraudPoint risk-band cut-offs. This screen is where
 * they are set. The numbers drive the commit gate directly: a Moderate score
 * requires follow-up documents before an account can be opened, and a High
 * score blocks it outright.
 *
 * PROVENANCE of the shipped values (Low 750+ / Moderate 500-749 / High below
 * 500): legacy procedure "Online Applicants.pdf" pp.2-3, and RM
 * compliance-gating review decision 2.2.
 *
 * WHY THIS SCREEN EXISTS. Until 2026-09-23 the same score was banded three
 * ways — the enforcing gate at 750/500 (correct), the LexisNexis API adapter
 * at placeholder 800/500/400, and the LN-Bridge parser off LexisNexis's own
 * colour glyph. The RM review had already caught it (section 1.5): the bands
 * "are currently in different places that disagree for scores between 750 and
 * 799", so "the risk level a banker sees could differ from the level the
 * system acts on". There is now one source, and this is its editor.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FRAUDPOINT_MAX,
  FRAUDPOINT_MIN,
  PROCEDURE_LOW_FLOOR,
  PROCEDURE_MODERATE_FLOOR,
  classify,
  loadFraudPointBands,
  saveFraudPointBands,
  validateBands,
  type FraudPointBandSettings,
} from '@/services/fraudPointBandsApi';
import { REASON_MAX, REASON_MIN } from '@/services/riskSettingsApi';

import { LnDisplaySettingsCard } from './LnDisplaySettingsCard';
import { VerificationTester } from './VerificationTester';

// LNRS is consolidating every service endpoint from lexisnexis.com onto
// lexisnexisrisk.com (Transition FAQs.pdf). The retiring URLs begin
// decommissioning in Q4 2026, with changes recommended before 2026-10-15, so
// this points at the DESTINATION host.
//
// The PCB LN Bridge extension injects its button only on hosts in its manifest
// match list. Extension 2.2.0+ matches BOTH hosts; 2.1.6 matched the legacy one
// only, which is why launching here showed no button until the extension was
// updated (Bill 2026-09-23). Pilot devices must run 2.2.0 or later.
const LEXISNEXIS_AML_INSIGHT_URL = 'https://amlinsight.lexisnexisrisk.com/';

/** Scores worth showing a reviewer: boundaries, plus the run that exposed the defect. */
const PREVIEW_SCORES = [834, 783, 757, 750, 749, 627, 500, 499, 350];

const BAND_COLOR: Record<string, string> = {
  Low: '#386732',
  Moderate: '#8a6d00',
  High: '#b10505',
};

export function LexisNexisScreen(): JSX.Element {
  const [loaded, setLoaded] = useState<FraudPointBandSettings | null>(null);
  const [lowFloor, setLowFloor] = useState<string>(String(PROCEDURE_LOW_FLOOR));
  const [moderateFloor, setModerateFloor] = useState<string>(String(PROCEDURE_MODERATE_FLOOR));
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  // Every Risk settings change carries a reason for the change log (2026-10-07).
  const [reason, setReason] = useState('');

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const bands = await loadFraudPointBands();
      setLoaded(bands);
      setLowFloor(String(bands.lowFloor));
      setModerateFloor(String(bands.moderateFloor));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the cut-offs.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const parsedLow = Number.parseInt(lowFloor, 10);
  const parsedModerate = Number.parseInt(moderateFloor, 10);
  const validation = useMemo(
    () =>
      Number.isFinite(parsedLow) && Number.isFinite(parsedModerate)
        ? validateBands(parsedLow, parsedModerate)
        : 'Enter both cut-offs.',
    [parsedLow, parsedModerate],
  );

  const dirty =
    loaded != null && (parsedLow !== loaded.lowFloor || parsedModerate !== loaded.moderateFloor);

  const handleSave = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await saveFraudPointBands(parsedLow, parsedModerate, reason.trim(), loaded ?? undefined);
      setReason('');
      // Reload rather than trust the write: the two keys save as two calls,
      // so the screen must show what actually landed.
      await refresh();
      setSaved('Cut-offs saved. They apply to the next screening — nothing already decided is re-graded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
      setBusy(false);
    }
  };

  const handleOpenLN = (): void => {
    const features =
      'width=1280,height=860,resizable=yes,scrollbars=yes,status=yes,location=yes,toolbar=no,menubar=no';
    window.open(LEXISNEXIS_AML_INSIGHT_URL, 'lexisnexis-aml-popup', features);
  };

  return (
    <>
      <section className="page-header">
        <h1 className="page-header__title">LexisNexis FraudPoint</h1>
        <p className="page-header__subtitle">
          Risk Management sets the score cut-offs that decide whether an account opening
          proceeds, pauses for more documents, or is declined.
        </p>
        <button type="button" onClick={handleOpenLN} className="btn btn--ghost" style={{ marginTop: 12 }}>
          Open LexisNexis AML Insight
        </button>
      </section>

      {error && (
        <section className="banner banner--err" role="alert">
          {error}
        </section>
      )}
      {saved && (
        <section className="banner" role="status" style={{ borderLeft: `4px solid ${BAND_COLOR.Low}` }}>
          {saved}
        </section>
      )}

      <section className="card" data-tour="ln-cutoffs">
        <div className="card-header">
          <h2>Risk-band cut-offs</h2>
        </div>

        {loaded?.usingDefaults && (
          <p className="form-hint">
            No saved cut-offs found, so the procedure values are in force
            ({PROCEDURE_LOW_FLOOR} / {PROCEDURE_MODERATE_FLOOR}). Saving here records them explicitly.
          </p>
        )}

        <div className="form-row">
          <label htmlFor="fp-low">
            Low risk — proceed
            <span className="form-hint"> Score at or above this is cleared to open.</span>
          </label>
          <input
            id="fp-low"
            type="number"
            inputMode="numeric"
            min={FRAUDPOINT_MIN}
            max={FRAUDPOINT_MAX}
            value={lowFloor}
            disabled={busy}
            onChange={(e) => {
              setLowFloor(e.target.value);
              setSaved(null);
            }}
          />
        </div>

        <div className="form-row">
          <label htmlFor="fp-moderate">
            Moderate risk — follow-up required
            <span className="form-hint">
              {' '}
              Score at or above this (but below the Low cut-off) pauses for documents. Anything
              below it is High risk and is declined.
            </span>
          </label>
          <input
            id="fp-moderate"
            type="number"
            inputMode="numeric"
            min={FRAUDPOINT_MIN}
            max={FRAUDPOINT_MAX}
            value={moderateFloor}
            disabled={busy}
            onChange={(e) => {
              setModerateFloor(e.target.value);
              setSaved(null);
            }}
          />
        </div>

        {validation && (
          <p className="form-hint" style={{ color: BAND_COLOR.High }}>
            {validation}
          </p>
        )}

        {dirty && (
          <div className="form-row">
            <label htmlFor="fp-reason">
              Reason for the change
              <span className="form-hint"> Required. Saved to the change log with your name.</span>
            </label>
            <input
              id="fp-reason"
              type="text"
              maxLength={REASON_MAX}
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}

        <div className="modal__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy || !dirty}
            onClick={() => {
              if (loaded) {
                setLowFloor(String(loaded.lowFloor));
                setModerateFloor(String(loaded.moderateFloor));
                setSaved(null);
              }
            }}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !dirty || validation !== null || reason.trim().length < REASON_MIN}
            onClick={() => void handleSave()}
          >
            {busy ? 'Working…' : 'Save cut-offs'}
          </button>
        </div>

        {loaded && !loaded.usingDefaults && (
          <p className="form-hint">
            Last changed{' '}
            {loaded.lowFloorUpdatedAt ? new Date(loaded.lowFloorUpdatedAt).toLocaleString() : 'unknown'}
            {loaded.lowFloorUpdatedBy ? ` by ${loaded.lowFloorUpdatedBy}` : ''}.
          </p>
        )}
      </section>

      <section className="card">
        <div className="card-header">
          <h2>What these cut-offs do to a score</h2>
        </div>
        <p className="form-hint">
          Live preview of the values above — not what is currently saved.
        </p>
        <table>
          <thead>
            <tr>
              <th scope="col">FraudPoint score</th>
              <th scope="col">Band</th>
              <th scope="col">What the banker must do</th>
            </tr>
          </thead>
          <tbody>
            {PREVIEW_SCORES.map((score) => {
              const band = Number.isFinite(parsedLow) && Number.isFinite(parsedModerate) && !validation
                ? classify(score, parsedLow, parsedModerate)
                : '—';
              return (
                <tr key={score}>
                  <td>{score}</td>
                  <td style={{ color: BAND_COLOR[band] ?? 'inherit', fontWeight: 600 }}>{band}</td>
                  <td>
                    {band === 'Low' && 'Proceed. Collect one item only if address, SSN or DOB disagrees.'}
                    {band === 'Moderate' &&
                      'Pause. Government photo ID required, plus an SSN card if out of market.'}
                    {band === 'High' && 'Do not open. Decline and send the adverse-action letter.'}
                    {band === '—' && 'Fix the cut-offs above to see this.'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="card-header">
          <h2>Before you change these</h2>
        </div>
        <ul>
          <li>
            The shipped values come from the bank&rsquo;s own procedure (Online Applicants,
            pages 2&ndash;3) and were put to Risk Management as decision 2.2 of the
            compliance-gating review. Changing them departs from that procedure, so record why.
          </li>
          <li>
            A change applies to the <strong>next</strong> screening. Openings already decided are
            not re-graded.
          </li>
          <li>
            The cut-offs are used in one place, so the screen a banker sees and the rule that
            blocks a commit can no longer disagree.
          </li>
          <li>
            If these settings are ever removed or left in an impossible state, screening falls
            back to {PROCEDURE_LOW_FLOOR}/{PROCEDURE_MODERATE_FLOOR} rather than letting anything
            through ungraded.
          </li>
          <li>
            The score is <strong>not stable across repeated lookups</strong> — LexisNexis counts
            recent searches of an identity as a risk signal, so running the same person several
            times lowers their score. Re-running is not a way to confirm a result.
          </li>
        </ul>
      </section>

      <LnDisplaySettingsCard />

      <VerificationTester />
    </>
  );
}
